import type { LogoTone } from "../components/LogoMark";

export type Tag = { type: string; color: string };

export type EntryLink = { url: string; descr: string };

export type Entry = {
  startDate: string;
  endDate: string;
  company: string;
  role: string;
  description: string[];
  booleanList: Tag[];
  links: EntryLink[];
  /** Organization homepage. */
  url?: string;
  /** Path under /public; omit to fall back to `monogram`. */
  logo?: string;
  /** Set to "native" for full-color artwork that cannot be repainted white.
   * Defaults to "mono". */
  logoTone?: LogoTone;
  /** Text mark shown when no logo artwork is available. */
  monogram?: string;
  /** One-line overview used by the company section in place of `description`. */
  summary?: string;
  /** Languages, tools and engineering methods used in the role. */
  skills?: string[];
  gifUrl?: string;
};

// Company roles: rendered as minimal logo lockups (summary line only, no bullets).
export const COMPANIES: Entry[] = [
  {
    startDate: "August 2026",
    endDate: "Present",
    company: "SpaceX",
    role: "Launch Pad Fluids Software Graduate Engineer",
    url: "https://www.spacex.com",
    logo: "/Images/logos/spacex.svg",
    summary:
      "Building software for launch pad fluids systems supporting vehicle launch operations.",
    description: [
      "Developing software for launch pad fluids systems supporting vehicle launch operations."
    ],
    skills: [
      "C++",
      "Python",
      "Fluid Systems Modeling",
      "Ground Software",
      "Controls",
    ],
    booleanList: [
      { type: "Fluids Software", color: "#E3C17A" },
      { type: "Launch Operations", color: "#8AB4F8" },
    ],
    links: [],
  },
  {
    startDate: "May 2026",
    endDate: "August 2026",
    company: "Varda Space Industries",
    role: "Program Management Intern",
    url: "https://varda.com",
    logo: "/Images/logos/varda.png",
    logoTone: "native",
    summary:
      "Owned program scheduling for W-10 and later vehicles, integrating inputs across the full mission lifecycle from supply chain at mission start through reentry.",
    description: [
      "Owned program scheduling for W-10 and later vehicles, integrating inputs across the full mission lifecycle from supply chain at mission start through reentry."
    ],
    skills: [
      "Python",
      "Program Scheduling",
      "Systems Integration",
      "Data Analysis",
    ],
    booleanList: [
      { type: "Program Scheduling", color: "#8AB4F8" },
      { type: "Mission Lifecycle", color: "#A3C7A0" },
      { type: "Supply Chain", color: "#D4A17A" },
    ],
    links: [],
  },
  {
    startDate: "May 2025",
    endDate: "August 2025",
    company: "Varda Space Industries",
    role: "Program Management Intern",
    url: "https://varda.com",
    logo: "/Images/logos/varda.png",
    logoTone: "native",
    summary:
      "Built Python scheduling and CAD-parsing tooling for vehicle build and environmental testing, and delivered the SpaceX L-3.5 environmental testing report for the W-Series 5 spacecraft.",
    description: [
      "Automated vehicle build scheduling by developing a Python tool leveraging REST APIs to integrate engineering calendars, reducing manual scheduling by 10+ hours per week.",
      "Built a Python Flask API to parse vehicle CAD models into hierarchical tree structures, enabling efficient search and automated data generation for program deliverables.",
      "Coordinated environmental testing by designing and managing a custom test-stand scheduling platform, increasing utilization and reducing conflicts.",
      "Created SpaceX rideshare deliverables critical to the successful launch of Varda's W-Series 5 spacecraft, currently operating in orbit."
    ],
    skills: [
      "Python",
      "Flask",
      "REST APIs",
      "CAD Data Parsing",
      "Process Automation",
    ],
    booleanList: [
      { type: "Flask", color: "#D4A17A" },
      { type: "Python", color: "#A698A7" },
      { type: "REST APIs", color: "#8AB4F8" },
    ],
    links: [],
  },
];

// Clubs, competitions and research: rendered as a detailed timeline.
export const INVOLVEMENT: Entry[] = [
  {
    startDate: "August 2025",
    endDate: "May 2026",
    company: "Illinois Space Society",
    role: "Guidance, Navigation, and Control Team Lead",
    logo: "/Images/logos/illinois-space-society.png",
    logoTone: "native",
    description: [
      "Led team of 20 students in development of C++ Extended Kalman Filter for translational state estimation and Multiplicative Quaternion EKF for attitude determination, fusing sensor data for 2-stage rocket.",
      "Contributed to record launch height (75,000 ft) by building flight-critical C++ tilt calculator for second stage separation and debugging automated GPS tracking antenna during mission operations.",
      "Achieved 5% altitude state estimation error during 2-stage rocket flight through integrating IMU, barometer, and GPS data for accurate altitude predictions, improving previous model error by 30%.",
      "Derived and validated 6DOF dynamics, transition/rotation matrices, and finite state machine (FSM) transitions through Python Monte Carlo simulations, resolving inconsistencies and improving estimation stability."
    ],
    gifUrl: "/Images/rocket_animation.gif",
    skills: [
      "C++",
      "Python",
      "Extended Kalman Filter",
      "Sensor Fusion",
      "6DOF Dynamics",
      "Monte Carlo Simulation",
    ],
    booleanList: [
      { type: "C++", color: "#E3C17A" },
      { type: "Python", color: "#A698A7" },
      { type: "State Estimation", color: "#8AB4F8" },
    ],
    links: [
      {
        url: "https://github.com/ISSUIUC/GNC-SILSIM",
        descr: "Project Repository",
      },
      {
        url: "https://www.youtube.com/watch?v=5BYeBV-nO4Y",
        descr: "Launch Video",
      },
    ],
  },
  {
    startDate: "August 2025",
    endDate: "December 2025",
    company: "Laboratory for Advanced Space Systems at Illinois (LASSI)",
    role: "Guidance, Navigation, and Control Subteam Member",
    monogram: "LASSI",
    description: [
      "Competed in Vertical Landing Rocket challenge, building rocket capable of landing itself and awarded Best Landing Sequence through stable rocket dynamics by building fins, shock absorbers, and landing legs.",
      "Propagated satellite orbital trajectories to determine magnetic torques acting on CubeSat throughout flight.",
      "Investigated reaction wheels' saturation rate and capabilities to counteract environmental disturbances during orbit."
    ],
    skills: [
      "Python",
      "Orbit Propagation",
      "ADCS",
      "Reaction Wheel Sizing",
      "Magnetic Torque Modeling",
    ],
    booleanList: [
      { type: "ADCS", color: "#8AB4F8" },
      { type: "Orbital Mechanics", color: "#A3C7A0" },
      { type: "Python", color: "#A698A7" },
    ],
    links: [],
  },
  {
    startDate: "December 2024",
    endDate: "June 2025",
    company: "NASA Human Lander Challenge (HuLC)",
    role: "Software Team Lead",
    logo: "/Images/logos/nasa.svg",
    logoTone: "native",
    description: [
      "Achieved Best Technical Presentation Award at annual NASA conference by leading simulation development of a microgravity propellant transfer system.",
      "Proved two-phase microgravity propellant transfer by creating GFSSP/Python simulations by working with leading NASA experts, producing 15-20% propellant mass savings in transfer operations."
    ],
    skills: [
      "GFSSP",
      "Python",
      "Two-Phase Flow Modeling",
      "Thermal-Fluid Simulation",
      "Propellant Transfer",
    ],
    booleanList: [
      { type: "GFSSP", color: "#D4A17A" },
      { type: "Python", color: "#A698A7" },
      { type: "Simulation", color: "#8AB4F8" },
    ],
    links: [
      {
        url: "https://www.youtube.com/watch?v=Gxhk1hIIWIo",
        descr: "Proposal Video",
      },
      {
        url: "https://hulc.nianet.org/wp-content/uploads/2025-HuLC-University-of-Illinois-Urbana-Champaign-Technical-Paper.pdf",
        descr: "Technical Paper",
      },
      {
        url: "https://hulc.nianet.org/wp-content/uploads/2025-HuLC-University-of-Illinois-Urbana-Champaign-Digital-Poster.pdf",
        descr: "Technical Poster",
      },
    ],
  },
  {
    startDate: "August 2024",
    endDate: "December 2024",
    company: "NASA L'Space Mission Concept Academy",
    role: "Project Manager",
    logo: "/Images/logos/nasa.svg",
    logoTone: "native",
    description: [
      "Delivered a comprehensive lunar rover preliminary design review by managing a team of 12 students over 6 months.",
      "Maintained mission objectives during a 30% budget cut through successfully de-scoping across engineering teams.",
      "Executed mission planning by creating requirements, developing $300 million budget, and creating 7-year timeline."
    ],
    skills: [
      "NX CAD",
      "Requirements Development",
      "Mission Concept Design",
      "Cost & Schedule Modeling",
    ],
    booleanList: [
      { type: "Mission Planning", color: "#8AB4F8" },
      { type: "NX-CAD", color: "#A3C7A0" },
      { type: "Project Management", color: "#D38DA6" },
    ],
    links: [
      {
        url: "https://www.lspace.asu.edu/",
        descr: "Program Website",
      },
    ],
  },
];

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Focus tags with anything already listed under `skills` stripped out, so the
 * two chip rows never repeat a value ("NX-CAD" and "NX CAD" count as one).
 */
export function focusTags(entry: Entry): string[] {
  const covered = new Set((entry.skills ?? []).map(normalize));
  return entry.booleanList
    .map((tag) => tag.type)
    .filter((type) => !covered.has(normalize(type)));
}

// Back-compat: src/Website2_archive/* still consumes the flat CV export.
export const CV: Entry[] = [...COMPANIES, ...INVOLVEMENT];
